import React from 'react';

import {
  DetailPageWrapperProps,
  PageToolbar,
  PageToolbarProps,
  DrawerFilter,
} from '../../../../../base/presentation/components';
import { EnumStatusData } from '../../../../../base/domain/entities';
import {
  notificationFailed,
  notificationSuccess,
} from '../../../../../base/infrastructure/helpers';
import {
  useCurrentRoute,
  usePageProvider,
} from '../../../../../base/presentation/hooks';
import {
  DataTableContextEntity,
  DataTableProvider,
} from '../../../../../base/presentation/providers';
import { Form, Modal, Spin } from 'antd';
import { useEffect, useState } from 'react';
import { PageWrapper } from './page-wrapper';
import { LoadingOutlined } from '@ant-design/icons';

export function DetailPageWrapper(props: DetailPageWrapperProps) {
  const {
    children,
    padding,
    pageToolbarProps,
    dataDummy,
    forceRequest,
    paramGetData,
    showPageHeader = true,
  } = props;
  const [currentRoute] = useCurrentRoute();
  const [form] = Form.useForm();
  const [showContent, setShowContent] = useState(true);
  const [dataType, setDataType] = useState<'original' | 'requested'>(
    'original'
  );
  const {
    action,
    defaultDetailData,
    detailData,
    setDetailData,
    id,
    moduleKey,
  } = usePageProvider();
  const { navigation, dataSource, transformer } = usePageProvider();
  const { transformerGetData } = transformer;

  function makeDataDetail(payload: any, dataType = 'requested'): void {
    let newPayload = {};
    if (dataType === 'requested')
      newPayload = { ...payload.requested_data, id: payload.id };
    else newPayload = payload;
    const detailPayload = dataDummy ?? newPayload;
    form.setFieldsValue({ id, detail_data: detailPayload });
  }

  function transformerPayload(payload: any): void {
    let resultPayload = payload;
    const dataType = resultPayload.requested_data ? 'requested' : 'original';
    setDataType(dataType);
    if (transformerGetData) resultPayload = transformerGetData(payload);
    makeDataDetail(resultPayload, dataType);
    setDetailData(resultPayload);
  }

  function handleChangeDataType(value: any): void {
    setDataType(value);
    makeDataDetail(detailData, value);
  }

  async function handleGetData(): Promise<void> {
    await dataSource.handleGetData(id, {
      params: paramGetData,
      onSuccess: ({ response }: any) => {
        transformerPayload(response);
      },
      onFailed: ({ message }: any) => {
        notificationFailed(message);
      },
    });
  }

  function requestScheme(showData): void {
    if (dataDummy) transformerPayload(dataDummy);
    else if ((forceRequest || !showData) && id) handleGetData();
    else if (showData) transformerPayload(showData);
    else form.setFieldsValue({ form_action: action });
  }

  useEffect(() => {
    requestScheme(defaultDetailData);
  }, [forceRequest, defaultDetailData, id, dataDummy]);

  async function handleDelete(): Promise<void> {
    const payload = form.getFieldValue('detail_data');
    await dataSource.handleDelete(payload, {
      onSuccess: ({ response }: any) => {
        notificationSuccess([response]);
        navigation.goToIndex();
      },
      onFailed: ({ message }: any) => {
        notificationFailed([message]);
      },
    });
  }

  async function handleActivate(): Promise<void> {
    const payload = form.getFieldValue('detail_data');
    await dataSource.handleActivate(payload, {
      onSuccess: ({ response }: any) => {
        transformerPayload(response);
        notificationSuccess(['Successfully activate data.']);
      },
      onFailed: ({ message }: any) => {
        notificationFailed([message]);
      },
    });
  }

  async function handleDeactivate(): Promise<void> {
    const payload = form.getFieldValue('detail_data');
    await dataSource.handleDeactivate(payload, {
      onSuccess: ({ response }: any) => {
        transformerPayload(response);
        notificationSuccess(['Successfully deactivate data.']);
      },
      onFailed: ({ message }: any) => {
        notificationFailed([message]);
      },
    });
  }

  async function handleConfirmProcess(): Promise<void> {
    const payload = form.getFieldValue('detail_data');
    await dataSource.handleConfirmProcess(payload, {
      onSuccess: ({ response }: any) => {
        transformerPayload(response);
        notificationSuccess(['Successfully confirm process data.']);
      },
      onFailed: ({ message }: any) => {
        notificationFailed(message);
      },
    });
  }

  function handleClickUpdate() {
    navigation.goToUpdate({ id: detailData.id });
  }

  function handleClickDuplicate() {
    navigation.goToDuplicate({ id: detailData.id });
  }

  function handleClickNewData() {
    navigation.goToCreate();
  }

  function handleClickBack() {
    navigation.goBack(currentRoute);
  }

  async function handleClickReload(): Promise<void> {
    setShowContent(false);
    await new Promise(resolve => setTimeout(resolve, 500));
    setShowContent(true);
  }

  function handleClickDelete(): void {
    Modal.confirm({
      mask: true,
      okText: 'Confirm',
      title: 'Delete Confirmation',
      content: 'Are you sure you want to delete this data?',
      onOk: () => handleDelete(),
    });
  }

  function handleClickConfirmProcess(): void {
    Modal.confirm({
      mask: true,
      okText: 'Confirm Process',
      title: 'Confirm Process Confirmation',
      content: 'Are you sure you want to confirm this data?',
      onOk: () => handleConfirmProcess(),
    });
  }

  function handleClickActivate(): void {
    Modal.confirm({
      mask: true,
      okText: 'Confirm',
      title: 'Activate Confirmation',
      content: 'Are you sure you want to activate this data?',
      onOk: () => handleActivate(),
    });
  }

  function handleClickDeactivate(): void {
    Modal.confirm({
      mask: true,
      okText: 'Confirm',
      title: 'Deactivate Confirmation',
      content: 'Are you sure you want to deactivate this data?',
      onOk: () => handleDeactivate(),
    });
  }

  function checkShowConfirmProcess(row): boolean {
    if (pageToolbarProps.showConfirmProcess)
      return pageToolbarProps.showConfirmProcess;
    else if (
      row?.status?.toLowerCase() === EnumStatusData.draft ||
      row?.requested_data
    )
      return true;
    return false;
  }

  function checkShowActivate(row): boolean {
    if (pageToolbarProps.showActivate) return pageToolbarProps.showActivate;
    else if (row?.status?.toLowerCase() === EnumStatusData.inactive)
      return true;
    return false;
  }

  function checkShowDeactivate(row): boolean {
    if (pageToolbarProps.showDeactivate) return pageToolbarProps.showDeactivate;
    else if (row?.status?.toLowerCase() === EnumStatusData.active) return true;
    return false;
  }

  const PageToolbarProperty: PageToolbarProps = {
    rowData: detailData,
    showDataTypeSelector: !!detailData?.requested_data,
    showApproval: false,
    showSearch: false,
    showActivate: checkShowActivate(detailData),
    showDeactivate: checkShowDeactivate(detailData),
    showConfirmProcess: checkShowConfirmProcess(detailData),
    showExport: false,
    showImport: false,
    showPrint: false,
    showSave: false,
    showFilter: false,
    dataTypeValue: dataType,

    handleClickBack,
    handleClickReload,
    handleClickNewData,
    handleClickDelete,
    handleClickActivate,
    handleClickDeactivate,
    handleClickConfirmProcess,
    handleClickUpdate,
    handleClickDuplicate,
    handleChangeDataType,
    ...pageToolbarProps,
  };

  return (
    <div>
      {showPageHeader && <PageToolbar {...PageToolbarProperty} />}
      <PageWrapper padding={padding}>
        <Spin
          spinning={!showContent}
          indicator={<LoadingOutlined />}
          style={{ minHeight: '50vh' }}
        >
          <Form layout="vertical" form={form} name={`form-detail-${moduleKey}`}>
            <Form.Item noStyle hidden name="id"></Form.Item>
            <Form.Item noStyle hidden name="detail_data"></Form.Item>
            {showContent && children}
          </Form>
        </Spin>
      </PageWrapper>
    </div>
  );
}
